import { IQuestionnaireGroup, IQuestionnaireItem, IQuestionnaireWorkflowItem } from '@interfaces/interfaces';
import { SessionItemData } from '@workflow/services/session-item-data';
import { BlockQuestionItem, SpecialQuestionItem } from '@interfaces/questions';
import { SpecialQuestionService } from '@workflow/components/questionnaire/questionnaire-item/special-question/special-question.service';
import { Injectable } from '@angular/core';
import { MessplatzService } from '@app/services/messplatz/messplatz.service';
import { isNil } from 'lodash';
import {MissingValueService} from '@app/services/missing-value/missing-value.service';

export interface GroupStatus {
	status: 'unstarted' | 'finished' | 'edited' | 'disabled';
	isSkippable?: boolean;
	isSufficient?: boolean;
	blocks: ('unstarted' | 'finished' | 'edited' | 'disabled')[][];
	hasMissingValue: boolean;
}

export type WorkflowItemStatus = {
	finished: IQuestionnaireGroup[];
	unfinished: IQuestionnaireGroup[];
	unstarted: IQuestionnaireGroup[];
	disabled: IQuestionnaireGroup[];
};

type MissingValueStatus = {
	missingValuesForKeys: {
		[key: string]: boolean
	};
	allItemsHaveMissingValues: boolean;
};

@Injectable({
	providedIn: 'root',
})
export class QuestionStatusService {

	constructor(
		private specialQuestionService: SpecialQuestionService,
		private messplatzService: MessplatzService,
	) {}

	private missingValueStatus(items: IQuestionnaireItem[], data: SessionItemData): MissingValueStatus {
		const returnObj: MissingValueStatus = {missingValuesForKeys: {}, allItemsHaveMissingValues: true};
		for (const item of items) {
			const hasItemMissingValue = MissingValueService.isMissingValue(item, data);

			if (item.type === 'BlockQuestion') {
				const blockQuestions = (item as BlockQuestionItem).items || [];
				blockQuestions.forEach(blockQuestion => returnObj.missingValuesForKeys[blockQuestion.key] = hasItemMissingValue);
			} else if (this.specialQuestionService.isSpecialQuestion(item.type)) {
				const key = (item as SpecialQuestionItem).key;
				returnObj.missingValuesForKeys[key] = hasItemMissingValue;
			}
			if (!hasItemMissingValue) {
				returnObj.allItemsHaveMissingValues = false;
			}
		}

		return returnObj;
	}

	public checkQuestionGroupStatus(group: IQuestionnaireGroup, data: SessionItemData, items: IQuestionnaireItem[] = group?.items || []): GroupStatus {
		let finished = 0;
		let edited = 0;
		const keysOfQuestionOptionalsAsValue: Record<string, boolean> = {};
		const keysOfQuestionWithMissingValue: Record<string, boolean> = {};
		const keysOfQuestionOptionalsAsValueMappedIndex: Record<string, number> = {};
		const keysOfQuestionSkippable: Record<string, boolean> = {};
		const specialQuestions: Record<string, SpecialQuestionItem> = {};

		const { missingValuesForKeys, allItemsHaveMissingValues } = this.missingValueStatus(items, data);
		const hasGroupMissingValue = MissingValueService.isMissingValue(group, data) || allItemsHaveMissingValues;

		const groupStatus: GroupStatus = {
			status: null,
			isSkippable: true,
			isSufficient: true,
			blocks: [],
			hasMissingValue: hasGroupMissingValue,
		};

		if (!this.checkGroupEnabled(group, data)) {
			return {
				status: 'disabled',
				isSkippable: true,
				isSufficient: true,
				blocks: [],
				hasMissingValue: hasGroupMissingValue,
			};
		}

		let groupStatusIndex = -1;
		for (const item of items) {
			if (item.showIf && data && !data.getProperty(item.showIf)) {
				continue;
			}
			//Alle Frage Keys und ob die Frage optional ist werden gespeichert.
			groupStatusIndex++;
			groupStatus.blocks.push([]);
			if (item.type === 'BlockQuestion') {
				const blockQuestionItem = item as BlockQuestionItem;
				if (blockQuestionItem.options?.isCompletableWhenEmpty) {
					const allQuestionsHiddenOrNil = blockQuestionItem.items.every(bq => 
						(bq.showIf && data && !data.getProperty(bq.showIf)) || isNil(data.getProperty(bq.key)));
					if (allQuestionsHiddenOrNil) {
						continue;
					}
				}
				for (const blockQuestion of blockQuestionItem.items) {
					if (blockQuestion.showIf && data && !data.getProperty(blockQuestion.showIf)) {
						continue;
					}
					keysOfQuestionOptionalsAsValue[blockQuestion.key] = blockQuestion.question?.isAnswerOptional;
					keysOfQuestionWithMissingValue[blockQuestion.key] = !!missingValuesForKeys[blockQuestion.key];
					keysOfQuestionSkippable[blockQuestion.key] = blockQuestion.question?.isWizardSkipAllowed;
					keysOfQuestionOptionalsAsValueMappedIndex[blockQuestion.key] = groupStatusIndex;
				}
			} else if (this.specialQuestionService.isSpecialQuestion(item.type)) {
				const specialQuestion = item as SpecialQuestionItem;
				keysOfQuestionOptionalsAsValue[specialQuestion.key] = false;
				keysOfQuestionSkippable[specialQuestion.key] = false;
				specialQuestions[specialQuestion.key] = specialQuestion;
				keysOfQuestionOptionalsAsValueMappedIndex[specialQuestion.key] = 0;
			}
		}

		for (const key in keysOfQuestionOptionalsAsValue) {
			const storedData = data?.getProperty(key);
			const specialQuestion = specialQuestions[key];
			let enoughValues = true;
			if (specialQuestion) {
				enoughValues = this.specialQuestionService.getSpecialQuestionCompleted(group, specialQuestion, storedData, data);
			} else {
				enoughValues = !isNil(storedData) || keysOfQuestionOptionalsAsValue[key];
			}

			const index = keysOfQuestionOptionalsAsValueMappedIndex[key];
			if (enoughValues || hasGroupMissingValue || keysOfQuestionWithMissingValue[key]) {
				finished++;
				groupStatus.blocks[index].push('finished');
			} else {
				edited += !isNil(storedData) ? 1 : 0;

				if (!keysOfQuestionOptionalsAsValue[key]) {
					groupStatus.isSufficient = false;
				}
				if (!keysOfQuestionSkippable[key] && !keysOfQuestionOptionalsAsValue[key]) {
					groupStatus.isSkippable = false;
				}

				if (isNil(storedData)) {
					groupStatus.blocks[index].push('unstarted');
				} else {
					groupStatus.blocks[index].push('edited');
				}
			}
		}

		if (finished === Object.keys(keysOfQuestionOptionalsAsValue).length || groupStatus.isSufficient) {
			groupStatus.status = 'finished';
		} else if (finished + edited === 0) {
			groupStatus.status = 'unstarted';
		} else {
			groupStatus.status = 'edited';
		}

		return groupStatus;
	}

	checkGroupEnabled(group: IQuestionnaireGroup, data: SessionItemData): boolean {
		if (group.showIfDeviceAbsent && this.messplatzService.hasAllDevices(group.showIfDeviceAbsent)) {
			return false;
		}
		if (group.showIfDevicePresent && !this.messplatzService.hasAllDevices(group.showIfDevicePresent)) {
			return false;
		}
		if (group.showIf && data && !data.getProperty(group.showIf)) {
			return false;
		}
		if (!group.items?.length) {
			return false;
		}
		return true;
	}

	checkWorkflowItemStatus(workflowItem: IQuestionnaireWorkflowItem, itemData: SessionItemData): WorkflowItemStatus {
		const status: WorkflowItemStatus = {
			finished: [],
			unfinished: [],
			unstarted: [],
			disabled: [],
		};

		for (const group of workflowItem.groups) {
			const groupStatus = this.checkQuestionGroupStatus(group, itemData);
			switch (groupStatus.status) {
				case 'disabled':
					status.disabled.push(group);
					break;
				case 'finished':
					status.finished.push(group);
					break;
				case 'unstarted':
					status.unfinished.push(group);
					status.unstarted.push(group);
					break;
				case 'edited':
				default:
					status.unfinished.push(group);
					break;
			}
		}

		return status;
	}
}
